import { SearchOutlined } from '@ant-design/icons'
import { Button, Input, message, Space, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import * as _ from 'lodash'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import Highlighter from 'react-highlight-words'

import { convertTreeDataToDataSource } from './data-transform'
import { getItemNestedProperty } from './helpers'
import { DnDTreeTableProps } from './types'
import { TreeItem } from '../arrayToTree'
import { TreeNodes } from '../dnd-tree'

const { Text } = Typography
const { Search } = Input

const useDndTreeTable = (props: DnDTreeTableProps) => {
    const [flatTreeNodes, setFlatTreeNodes] = useState<TreeNodes>([])
    const [dataSource, setDataSource] = useState<TreeItem[]>(
        convertTreeDataToDataSource(props.treeNodes)
    )
    const [state, setState] = React.useState({
        searchText: '',
        searchedColumn: ''
    })

    useEffect(() => {
        setFlatTreeNodes(props.treeNodes)
    }, [props.treeNodes])

    useEffect(() => {
        setDataSource(convertTreeDataToDataSource(flatTreeNodes))
    }, [flatTreeNodes])

    const [showNotFoundMsg, setShowNotFoundMsg] = useState(false)

    // filter nodes finding all ancestors of provided array of ids.
    const filterNodes = (nodes: string[]) => {
        const ars: string[][] = []

        nodes.map((n) => {
            ars.push(getAncestorsFromOrderedArray(n))
        })

        const filteredNodeIds: string[] = _.union(...ars)
        const filteredFlatTreeNodes: TreeNodes = []

        filteredNodeIds.map((id) => {
            filteredFlatTreeNodes.push(
                // @ts-ignore
                flatTreeNodes.find((n) => n.data.id === id)
            )
        })
        setFlatTreeNodes(filteredFlatTreeNodes)
    }

    useEffect(() => {
        if (showNotFoundMsg) {
            setShowNotFoundMsg(false)
        }
    }, [showNotFoundMsg])

    const onFilter = useCallback(
        (value, dataIndex, properties: string[]) => {
            if (value !== '' && value !== undefined && value !== null) {
                // List all node ids that contains the searched value within any of provided props
                const nodeIdsMatchingSearchedValues: string[] = []

                flatTreeNodes.map((n) => {
                    // 'some' allows to exit as soon as 1 prop contains the value
                    const valueIsMatched = properties.some((prop) => {
                        return (n.data[prop] as string)
                            .toString()
                            .toLowerCase()
                            .includes(value.toLowerCase())
                    })
                    if (valueIsMatched) {
                        nodeIdsMatchingSearchedValues.push(n.data.id)
                    }
                })

                if (nodeIdsMatchingSearchedValues.length > 0) {
                    filterNodes(nodeIdsMatchingSearchedValues)
                    setShowNotFoundMsg(false)
                } else {
                    setShowNotFoundMsg(true)
                }
            }
        },
        [flatTreeNodes]
    )

    const reset = useCallback(() => {
        setShowNotFoundMsg(false)
        setFlatTreeNodes(props.treeNodes)
        setState({ searchText: '', searchedColumn: '' })
    }, [props.treeNodes])

    const getAncestorsFromOrderedArray = (nodeId: string) => {
        // Our array is ordered. Hence by reversing it, we will just have to iterate each node
        // from the nodeId, to find all ancestors
        const reverseFlatTreeNodes = flatTreeNodes.slice().reverse() // slice avoids mutating original array
        const nodeIndex = reverseFlatTreeNodes.findIndex(
            (n) => n.data.id === nodeId
        )
        const node = reverseFlatTreeNodes[nodeIndex]
        let nodeParentId = node.data.parent

        const filteredFlatTreeNodes: string[] = []
        filteredFlatTreeNodes.push(node.data.id)

        for (let i = nodeIndex + 1; i < reverseFlatTreeNodes.length; i++) {
            const n = reverseFlatTreeNodes[i]
            if (n.data.id === nodeParentId) {
                filteredFlatTreeNodes.push(n.data.id)
                nodeParentId = n.data.parent
            }
        }
        return filteredFlatTreeNodes.reverse()
    }

    const search = useCallback((searchText, dataIndex) => {
        reset()
        setState({
            searchText: searchText,
            searchedColumn: dataIndex
        })
    }, [])

    return {
        reset,
        dataSource,
        onFilter,
        search,
        state,
        showNotFoundMsg
    }
}

export const DnDTreeTable: React.FC<DnDTreeTableProps> = (props) => {
    const {
        reset,
        dataSource,
        onFilter,
        search,
        state,
        showNotFoundMsg
    } = useDndTreeTable(props)

    // This completely replaces columns' onFilter
    useEffect(() => {
        onFilter(state.searchText, state.searchedColumn, ['title', 'wbsId'])
    }, [state.searchText])

    if (showNotFoundMsg) {
        message.info(`No match for ${state.searchText}`)
    }

    const getColumnSearchProps = (dataIndex: any) => {
        return {
            title: (
                <TitleRender
                    dataIndex={dataIndex}
                    handleSearch={handleSearch}
                />
            ),
            render: (data: any) => (
                <TitleRow
                    searchText={state.searchText}
                    title={data.title}
                    wbsId={data.wbsId}
                />
            )
        }
    }

    const handleSearch = (searchText: any, dataIndex: any) => {
        search(searchText, dataIndex)
    }

    const columns: ColumnsType = [
        {
            // title: 'Title',
            dataIndex: 'data',
            key: 'title',
            ...getColumnSearchProps('data')
        }
    ]

    return (
        <TestInner
            columns={columns}
            dataSource={dataSource}
            reset={reset}
            onRowClick={props.onRowClick}
        />
    )
}

interface TitleRenderProps {
    dataIndex: string
    handleSearch: (searchText: string, dataIndex: string) => void
}
const TitleRender: React.FC<TitleRenderProps> = (props) => {
    const { dataIndex, handleSearch } = props
    const [searchVal, setSearchVal] = useState('')

    return (
        <Search
            placeholder={`Search hierarchy`}
            style={{ width: 188 }}
            onChange={(e) => {
                setSearchVal(e.target.value)
            }}
            onSearch={() => {
                handleSearch(searchVal, dataIndex)
            }}
        />
    )
}

interface TestInnerProps {
    columns: any
    dataSource: any
    onRowClick?: (nodeId: string, node: any) => void
    reset: any
}

const TestInner: React.FC<TestInnerProps> = (props) => {
    const { columns, dataSource, onRowClick, reset } = props
    return (
        <React.Fragment>
            <Button onClick={() => reset()}>Clear Filters</Button>
            <Table
                dataSource={dataSource}
                columns={columns}
                expandable={{ defaultExpandAllRows: true }}
                scroll={{ y: 500 }}
                pagination={false}
                onRow={(node, index) => ({
                    onClick: () => onRowClick && onRowClick(node.data.id, node)
                })}
                size="small"
            />
        </React.Fragment>
    )
}

const titleColumnHeaderPropsAreEqual = (
    prevProps: Readonly<React.PropsWithChildren<TitleColumnHeaderProps>>,
    nextProps: Readonly<React.PropsWithChildren<TitleColumnHeaderProps>>
) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps)
}

interface TitleColumnHeaderProps {
    searchText: string
    wbsId: string
    title: string
}

const TitleRow: React.FC<TitleColumnHeaderProps> = React.memo((props) => {
    const { searchText, title, wbsId } = props
    return (
        <Space>
            <Text type="secondary">
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={wbsId}
                />
            </Text>
            <Text strong>
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={title}
                />
            </Text>
        </Space>
    )
}, titleColumnHeaderPropsAreEqual)
